import { countryJsonType } from "../ReduxTool/Slice/Auth/types";
import { baseURL, requestUrl } from "./baseUrls";

let countryListRequest: Promise<countryJsonType[]> | null = null;

export const loadCountryMaster = (): Promise<countryJsonType[]> => {
  if (!countryListRequest) {
    countryListRequest = baseURL
      .get(requestUrl.getCountryCode + "?startRecordIndex=0&numberOfRecords=250")
      .then(({ data }) => data?.responseData?.data ?? [])
      .catch(error => {
        countryListRequest = null;
        throw error;
      });
  }
  return countryListRequest;
};

const countryAliases: Record<string, string> = {
  usa: "US",
  "united states of america": "US",
  uae: "AE",
  "\u0639\u0645\u0627\u0646": "OM",
  "\u0633\u0644\u0637\u0646\u0629 \u0639\u0645\u0627\u0646": "OM",
  "\u0627\u0644\u0625\u0645\u0627\u0631\u0627\u062a \u0627\u0644\u0639\u0631\u0628\u064a\u0629 \u0627\u0644\u0645\u062a\u062d\u062f\u0629": "AE",
  "\u0627\u0644\u0645\u0645\u0644\u0643\u0629 \u0627\u0644\u0639\u0631\u0628\u064a\u0629 \u0627\u0644\u0633\u0639\u0648\u062f\u064a\u0629": "SA",
};

export const findCountryMaster = (countries: countryJsonType[], nameOrCode?: string | null) => {
  const key = (nameOrCode ?? "").trim().toLowerCase();
  const code = countryAliases[key] ?? key.toUpperCase();
  return countries.find(country => country.alpha2code?.toUpperCase() === code ||
    country.iso3code?.toUpperCase() === code ||
    country.countryname?.toLowerCase() === key);
};

// Existing frontend postal rules, used when country_mstr has no usable rule.
const legacyPostalRules: Record<string, { pattern: RegExp; fixedLength?: number; expected: string }> = {
  IN: { pattern: /^[1-9][0-9]{5}$/, fixedLength: 6, expected: "6 digits, starting with 1–9" },
  US: { pattern: /^\d{5}(-\d{4})?$/, expected: "5 digits or ZIP+4 (12345-6789)" },
  AE: { pattern: /^\d{5}$/, fixedLength: 5, expected: "5 digits" },
  SA: { pattern: /^\d{5}$/, fixedLength: 5, expected: "5 digits" },
  OM: { pattern: /^\d{3}$/, fixedLength: 3, expected: "3 digits" },
  AU: { pattern: /^\d{4}$/, fixedLength: 4, expected: "4 digits" },
};

// Some country_mstr rows describe no postal rule with format "NONE" and a regex
// that accepts every value. Those placeholders must not bypass the local rule.
const usablePostalRegex = (country?: countryJsonType | null): string | null => {
  const pattern = country?.postal_code_regex?.trim() ?? "";
  return pattern && !["^.*$", ".*", "^.+$", ".+"].includes(pattern) ? pattern : null;
};

export const postalInputMaxLength = (country?: countryJsonType | null): number | undefined => {
  const format = country?.postal_code_format ?? "";
  const pattern = usablePostalRegex(country);
  const match = pattern?.match(/^\^\(\\d\{(\d+)\}\)\$$/);
  if (pattern) {
    return /^#+$/.test(format) && match && Number(match[1]) === format.length ? format.length : undefined;
  }
  if (/^#+$/.test(format)) return format.length;
  return legacyPostalRules[country?.alpha2code?.toUpperCase() ?? ""]?.fixedLength;
};

export const normalizeCountryPostalInput = (value: string, country?: countryJsonType | null): string => {
  const fixedNumericLength = postalInputMaxLength(country);
  return fixedNumericLength ? value.replace(/\D/g, "").slice(0, fixedNumericLength) : value;
};

export const validateCountryPostalCode = (value: string, country?: countryJsonType | null): string | null => {
  const code = (value ?? "").trim();
  if (!code) return "Zip/Postal code is required";
  if (!country) return "Postal code rules are unavailable for this country. Please try again.";
  const pattern = usablePostalRegex(country);
  if (pattern) {
    try {
      if (new RegExp(pattern).test(code)) return null;
      return /^#+$/.test(country.postal_code_format ?? "")
        ? `Invalid postal code. Expected format: ${country.postal_code_format}`
        : `Invalid postal code for ${country.countryname}`;
    } catch {
      // A malformed API regex cannot validate a code; use the available format or local rule.
    }
  }
  if (/^#+$/.test(country.postal_code_format ?? "")) {
    const length = country.postal_code_format!.length;
    return new RegExp(`^\\d{${length}}$`).test(code)
      ? null
      : `Invalid postal code. Expected format: ${country.postal_code_format}`;
  }
  const fallback = legacyPostalRules[country.alpha2code?.toUpperCase() ?? ""];
  if (!fallback) return null;
  return fallback.pattern.test(code) ? null : `Invalid postal code for ${country.countryname}. Expected ${fallback.expected}.`;
};

export type CountryTimezone = { zoneName: string; gmtOffsetName?: string };

export const parseCountryTimezones = (value?: string | null): CountryTimezone[] => {
  if (!value) return [];
  const zones: CountryTimezone[] = [];
  const entries = value.match(/\{[^}]*\}/g) ?? [];
  for (const entry of entries) {
    const name = entry.match(/zoneName\s*:\s*'([^']+)'/)?.[1];
    const offset = entry.match(/gmtOffsetName\s*:\s*'([^']+)'/)?.[1];
    if (name) zones.push({
      zoneName: name.replace(/\\\//g, "/"),
      gmtOffsetName: offset?.replace(/\\u([0-9a-fA-F]{4})/g, (_, hex) => String.fromCharCode(parseInt(hex, 16))),
    });
  }
  return zones;
};
